import React, { useState, useEffect, useCallback } from "react";
import { BookOpen, Plus, Trash2, RefreshCw, Search, Edit2, Save, X, Loader2, FlaskConical, ToggleLeft, ToggleRight, Sparkles, AlertTriangle } from "lucide-react";
import { Form, Table } from "react-bootstrap";
import { AppButton, AppCard, AppCardHeader, AppCardTitle, AppCardContent, AppBadge, EmptyState, AppModal, ConfirmModal } from "../ui";
import apiClient from "../../utils/apiClient";

const SOURCE_OPTIONS = [
  { value: "manual", label: "Manual" },
  { value: "faq", label: "FAQ" },
  { value: "course", label: "Course" },
  { value: "fee", label: "Fee" },
  { value: "policy", label: "Policy" },
  { value: "import", label: "Import" },
];

const KnowledgeBaseTab = ({ showToast }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // chunk being edited or null for new
  const [form, setForm] = useState({ title: "", text: "", source: "manual", tags: "", enabled: true });
  const [saving, setSaving] = useState(false);

  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [stale, setStale] = useState({ total: 0, stale: [] });
  const [showStale, setShowStale] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkTagInput, setBulkTagInput] = useState("");

  // Confirm dialogs (theme-aware)
  const [confirmDialog, setConfirmDialog] = useState(null); // { title, message, variant, onConfirm }
  const closeConfirm = () => setConfirmDialog(null);

  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (search.trim()) params.set("q", search.trim());
      if (sourceFilter) params.set("source", sourceFilter);
      const { data } = await apiClient.get(`/ai/whatsapp/knowledge?${params.toString()}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load knowledge base");
    } finally {
      setLoading(false);
    }
  }, [page, search, sourceFilter]);

  useEffect(() => { load(); }, [load]);

  const loadStale = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/ai/whatsapp/knowledge/stale?limit=100`);
      setStale({ total: data.total || 0, stale: data.stale || [] });
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => { loadStale(); }, [loadStale]);

  // Reset selection when items change (page/filter change)
  useEffect(() => { setSelectedIds(new Set()); }, [items]);

  const allOnPageSelected = items.length > 0 && items.every((c) => selectedIds.has(c._id));
  const toggleSelect = (id) => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAllOnPage = () => {
    setSelectedIds((s) => {
      const next = new Set(s);
      if (allOnPageSelected) items.forEach((c) => next.delete(c._id));
      else items.forEach((c) => next.add(c._id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const runBulk = async (action, extra = {}) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    const performBulk = async () => {
      setBulkBusy(true);
      try {
        const { data } = await apiClient.post(`/ai/whatsapp/knowledge/bulk-update`, { ids, action, ...extra });
        const n = data.modified ?? data.deleted ?? 0;
        showToast(`Bulk ${action}: ${n} chunk(s) updated`, "success");
        clearSelection();
        load();
      } catch (err) {
        showToast(err?.response?.data?.message || "Bulk action failed", "error");
      } finally { setBulkBusy(false); closeConfirm(); }
    };
    if (action === "delete") {
      setConfirmDialog({
        title: `Delete ${ids.length} chunk(s)?`,
        message: "This action cannot be undone. The selected knowledge chunks will be permanently removed.",
        variant: "danger",
        confirmText: "Delete",
        onConfirm: performBulk,
      });
      return;
    }
    performBulk();
  };

  const openNew = () => {
    setEditing(null);
    setForm({ title: "", text: "", source: "manual", tags: "", enabled: true });
    setShowForm(true);
  };
  const openEdit = (chunk) => {
    setEditing(chunk);
    setForm({
      title: chunk.title || "",
      text: chunk.text || "",
      source: chunk.source || "manual",
      tags: (chunk.tags || []).join(", "),
      enabled: chunk.enabled !== false,
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.text.trim()) { showToast("Text is required", "error"); return; }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        text: form.text.trim(),
        source: form.source,
        tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
        enabled: form.enabled,
      };
      if (editing) {
        await apiClient.put(`/ai/whatsapp/knowledge/${editing._id}`, payload);
        showToast("Updated", "success");
      } else {
        await apiClient.post(`/ai/whatsapp/knowledge`, payload);
        showToast("Added — embedding generated", "success");
      }
      setShowForm(false);
      load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Save failed", "error");
    } finally { setSaving(false); }
  };

  const remove = (chunk) => {
    setConfirmDialog({
      title: "Delete knowledge chunk?",
      message: `"${chunk.title || chunk.text.slice(0, 60)}" will be permanently removed from the AI knowledge base.`,
      variant: "danger",
      confirmText: "Delete",
      onConfirm: async () => {
        try {
          await apiClient.delete(`/ai/whatsapp/knowledge/${chunk._id}`);
          load();
        } catch (err) {
          showToast(err?.response?.data?.message || "Delete failed", "error");
        } finally { closeConfirm(); }
      },
    });
  };

  const toggleEnabled = async (chunk) => {
    try {
      await apiClient.put(`/ai/whatsapp/knowledge/${chunk._id}`, { enabled: !chunk.enabled });
      load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Toggle failed", "error");
    }
  };

  const reembed = async (chunk) => {
    try {
      await apiClient.post(`/ai/whatsapp/knowledge/${chunk._id}/reembed`);
      showToast("Re-embedded", "success");
      load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Re-embed failed", "error");
    }
  };

  const runSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setSearchResults(null);
    try {
      const { data } = await apiClient.post(`/ai/whatsapp/knowledge/search`, { query: searchQuery.trim(), k: 5 });
      setSearchResults(data);
    } catch (err) {
      setSearchResults({ error: err?.response?.data?.message || "Search failed" });
    } finally { setSearching(false); }
  };

  const seedFromCatalog = async () => {
    setConfirmDialog({
      title: "Refresh KB from catalog?",
      message: "This generates and refreshes knowledge chunks from your courses and batches using OpenAI embeddings (small cost).",
      variant: "info",
      confirmText: "Refresh",
      onConfirm: async () => {
        closeConfirm();
        setSeeding(true);
        try {
          const { data } = await apiClient.post(`/ai/whatsapp/knowledge/seed`, { includeBatches: true });
          const r = data.result || {};
          const c = r.courses || {}; const b = r.batches || {};
          showToast(`Seeded: courses ${c.created || 0} new / ${c.updated || 0} updated · batches ${b.created || 0} new / ${b.updated || 0} updated`, "success");
          load();
          loadStale();
        } catch (err) {
          showToast(err?.response?.data?.message || "Seed failed", "error");
        } finally { setSeeding(false); }
      },
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="wa-kb">
      <AppCard style={{ marginBottom: 16 }}>
        <AppCardHeader>
          <AppCardTitle>
            <BookOpen size={16} /> Knowledge Base ({total})
            {stale.total > 0 && (
              <AppBadge variant="warning" style={{ marginLeft: 8, cursor: "pointer" }} onClick={() => setShowStale(true)} title="Click to view stale chunks">
                <AlertTriangle size={11} style={{ marginRight: 3 }} /> {stale.total} stale
              </AppBadge>
            )}
          </AppCardTitle>
          <div style={{ display: "flex", gap: 8 }}>
            <AppButton size="sm" variant="outline" onClick={seedFromCatalog} disabled={seeding} title={stale.total > 0 ? `Re-embed ${stale.total} stale chunk(s) + sync new courses/batches` : "Generate chunks from your courses & batches"}>
              {seeding ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />} {stale.total > 0 ? `Refresh stale (${stale.total})` : "Seed from catalog"}
            </AppButton>
            <AppButton size="sm" variant="outline" onClick={() => setShowSearch((s) => !s)}>
              <FlaskConical size={14} /> Test retrieval
            </AppButton>
            <AppButton size="sm" onClick={openNew}><Plus size={14} /> Add chunk</AppButton>
          </div>
        </AppCardHeader>
        <AppCardContent>
          <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center" }}>
            <div style={{ position: "relative", flex: 1, maxWidth: 320 }}>
              <Search size={14} style={{ position: "absolute", left: 8, top: 9, color: "#999" }} />
              <Form.Control
                size="sm"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                placeholder="Search title / text / tags..."
                style={{ paddingLeft: 28 }}
              />
            </div>
            <Form.Select size="sm" value={sourceFilter} onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }} style={{ maxWidth: 160 }}>
              <option value="">All sources</option>
              {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Form.Select>
            <AppButton size="sm" variant="outline" onClick={load} disabled={loading}>
              {loading ? <Loader2 size={14} className="spin" /> : <RefreshCw size={14} />}
            </AppButton>
          </div>

          {showSearch && (
            <div style={{ padding: 12, marginBottom: 12, border: "1px dashed var(--border)", borderRadius: 6 }}>
              <div style={{ display: "flex", gap: 8 }}>
                <Form.Control
                  size="sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="What would a customer ask?"
                />
                <AppButton size="sm" onClick={runSearch} disabled={searching || !searchQuery.trim()}>
                  {searching ? <Loader2 size={14} className="spin" /> : <Search size={14} />} Search
                </AppButton>
              </div>
              {searchResults && (
                <div style={{ marginTop: 10, fontSize: 13 }}>
                  {searchResults.error ? (
                    <div style={{ color: "var(--destructive, #c00)" }}>{searchResults.error}</div>
                  ) : searchResults.chunks?.length === 0 ? (
                    <div style={{ color: "#666" }}>No matches above threshold</div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {(searchResults.chunks || []).map((c, i) => (
                        <div key={c._id} style={{ padding: 8, background: "var(--muted, #f7f7f9)", borderRadius: 4 }}>
                          <div style={{ fontWeight: 600, fontSize: 12 }}>
                            [{i + 1}] {c.title || "(untitled)"} <AppBadge variant="secondary">{c.source}</AppBadge>
                            <span style={{ marginLeft: 8, color: "#666", fontWeight: 400 }}>score: {c.score}</span>
                          </div>
                          <div style={{ fontSize: 12, marginTop: 4, color: "#444" }}>{(c.text || "").slice(0, 220)}...</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {error && <div style={{ padding: 8, marginBottom: 8, background: "#fee2e2", color: "#991b1b", borderRadius: 4 }}>{error}</div>}

          {selectedIds.size > 0 && (
            <div style={{ padding: 10, marginBottom: 10, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 4, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <strong style={{ fontSize: 13 }}>{selectedIds.size} selected</strong>
              <AppButton size="sm" variant="outline" onClick={() => runBulk("enable")} disabled={bulkBusy}><ToggleRight size={12} /> Enable</AppButton>
              <AppButton size="sm" variant="outline" onClick={() => runBulk("disable")} disabled={bulkBusy}><ToggleLeft size={12} /> Disable</AppButton>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <Form.Control size="sm" value={bulkTagInput} onChange={(e) => setBulkTagInput(e.target.value)} placeholder="tag1, tag2" style={{ width: 140 }} />
                <AppButton size="sm" variant="outline" disabled={bulkBusy || !bulkTagInput.trim()} onClick={() => { runBulk("addTags", { tags: bulkTagInput.split(",").map((t) => t.trim()).filter(Boolean) }); setBulkTagInput(""); }}>+ Tag</AppButton>
                <AppButton size="sm" variant="outline" disabled={bulkBusy || !bulkTagInput.trim()} onClick={() => { runBulk("removeTags", { tags: bulkTagInput.split(",").map((t) => t.trim()).filter(Boolean) }); setBulkTagInput(""); }}>− Tag</AppButton>
              </div>
              <AppButton size="sm" variant="outline" onClick={() => runBulk("delete")} disabled={bulkBusy} style={{ color: "#c00", borderColor: "#c00" }}><Trash2 size={12} /> Delete</AppButton>
              <AppButton size="sm" variant="ghost" onClick={clearSelection} disabled={bulkBusy}><X size={12} /> Clear</AppButton>
              {bulkBusy && <Loader2 size={14} className="spin" />}
            </div>
          )}

          {items.length === 0 && !loading ? (
            <EmptyState
              icon={<BookOpen size={32} />}
              title="No knowledge chunks yet"
              description="Add Q&A pairs, course descriptions, fee policies, etc. The AI will use them to ground its replies."
            />
          ) : (
            <Table responsive hover size="sm" style={{ marginBottom: 0 }}>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>
                    <Form.Check type="checkbox" checked={allOnPageSelected} onChange={toggleSelectAllOnPage} title="Select all on this page" />
                  </th>
                  <th style={{ width: "30%" }}>Title</th>
                  <th>Text preview</th>
                  <th>Source</th>
                  <th>Tags</th>
                  <th style={{ textAlign: "right" }}>Hits</th>
                  <th style={{ width: 140 }}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c._id} style={{ opacity: c.enabled ? 1 : 0.5, background: selectedIds.has(c._id) ? "#eff6ff" : undefined }}>
                    <td>
                      <Form.Check type="checkbox" checked={selectedIds.has(c._id)} onChange={() => toggleSelect(c._id)} />
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{c.title || <span style={{ color: "#999" }}>(untitled)</span>}</div>
                      {c.embeddedAt && <div style={{ fontSize: 11, color: "#999" }}>embedded {new Date(c.embeddedAt).toLocaleDateString()}</div>}
                    </td>
                    <td style={{ fontSize: 12, color: "#444" }}>{(c.text || "").slice(0, 120)}{c.text?.length > 120 ? "..." : ""}</td>
                    <td><AppBadge variant="secondary">{c.source}</AppBadge></td>
                    <td style={{ fontSize: 11 }}>{(c.tags || []).join(", ")}</td>
                    <td style={{ textAlign: "right" }}>{c.hitCount || 0}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <AppButton size="xs" variant="ghost" onClick={() => toggleEnabled(c)} title={c.enabled ? "Disable" : "Enable"}>
                        {c.enabled ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                      </AppButton>
                      <AppButton size="xs" variant="ghost" onClick={() => reembed(c)} title="Re-embed"><RefreshCw size={12} /></AppButton>
                      <AppButton size="xs" variant="ghost" onClick={() => openEdit(c)}><Edit2 size={12} /></AppButton>
                      <AppButton size="xs" variant="ghost" onClick={() => remove(c)}><Trash2 size={12} color="#c00" /></AppButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          {totalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12 }}>
              <AppButton size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</AppButton>
              <span style={{ alignSelf: "center", fontSize: 13 }}>Page {page} / {totalPages}</span>
              <AppButton size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</AppButton>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      <AppModal
        show={showForm}
        onHide={() => setShowForm(false)}
        size="lg"
        title={editing ? "Edit knowledge chunk" : "Add knowledge chunk"}
        icon={editing ? <Edit2 size={16} /> : <Plus size={16} />}
        footer={(
          <>
            <AppButton variant="outline" onClick={() => setShowForm(false)}><X size={14} /> Cancel</AppButton>
            <AppButton onClick={submit} disabled={saving || !form.text.trim()}>
              {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />} {editing ? "Save" : "Add & embed"}
            </AppButton>
          </>
        )}
      >
        <Form.Group className="mb-3">
          <Form.Label>Title (optional)</Form.Label>
          <Form.Control value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g., Refund policy" />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Text *</Form.Label>
          <Form.Control as="textarea" rows={6} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="The full content the AI should know..." />
          <Form.Text muted>{form.text.length} / 8000 chars</Form.Text>
        </Form.Group>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 12 }}>
          <Form.Group>
            <Form.Label>Source</Form.Label>
            <Form.Select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
              {SOURCE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Tags (comma-separated)</Form.Label>
            <Form.Control value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="fees, refund, batch" />
          </Form.Group>
        </div>
        <Form.Check
          className="mt-3"
          type="switch"
          label="Enabled (included in AI context)"
          checked={form.enabled}
          onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
        />
      </AppModal>

      <AppModal
        show={showStale}
        onHide={() => setShowStale(false)}
        size="lg"
        title={`Stale knowledge chunks (${stale.total})`}
        icon={<AlertTriangle size={16} color="#d97706" />}
        footer={(
          <>
            <AppButton variant="outline" onClick={() => setShowStale(false)}>Close</AppButton>
            {stale.total > 0 && (
              <AppButton onClick={() => { setShowStale(false); seedFromCatalog(); }} disabled={seeding}>
                {seeding ? <Loader2 size={14} className="spin" /> : <Sparkles size={14} />} Refresh all stale
              </AppButton>
            )}
          </>
        )}
      >
        {stale.stale.length === 0 ? (
          <div style={{ color: "var(--text-secondary, #666)" }}>Nothing stale — all course/batch chunks are up-to-date.</div>
        ) : (
          <>
            <div style={{ marginBottom: 12, fontSize: 13, color: "var(--text-secondary, #444)" }}>
              These chunks were embedded before their source course/batch was updated (or the source was deleted).
              Click <strong>Refresh stale</strong> to re-embed them with the latest content.
            </div>
            <Table responsive hover size="sm">
              <thead>
                <tr>
                  <th>Chunk title</th>
                  <th>Source</th>
                  <th>Source title</th>
                  <th style={{ textAlign: "right" }}>Age (days)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {stale.stale.map((s) => (
                  <tr key={s.chunkId}>
                    <td style={{ fontSize: 12 }}>{s.title || <span style={{ color: "var(--text-tertiary, #999)" }}>(untitled)</span>}</td>
                    <td><AppBadge variant="secondary">{s.source}</AppBadge></td>
                    <td style={{ fontSize: 12 }}>
                      {s.orphaned ? <span style={{ color: "#c00" }}>orphaned (source deleted)</span> : (s.sourceTitle || "—")}
                    </td>
                    <td style={{ textAlign: "right", fontSize: 12 }}>{s.ageDays ?? "—"}</td>
                    <td style={{ textAlign: "right" }}>
                      <AppButton size="xs" variant="outline" onClick={() => reembed({ _id: s.chunkId })} title="Re-embed this chunk">
                        <RefreshCw size={11} />
                      </AppButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </AppModal>

      <ConfirmModal
        show={!!confirmDialog}
        onHide={closeConfirm}
        onConfirm={confirmDialog?.onConfirm}
        title={confirmDialog?.title}
        message={confirmDialog?.message}
        variant={confirmDialog?.variant || "danger"}
        confirmText={confirmDialog?.confirmText || "Confirm"}
        loading={bulkBusy || seeding}
      />
    </div>
  );
};

export default KnowledgeBaseTab;
